import {
  registerIVSTech,
  VideoJSQualityPlugin,
  VideoJSIVSTech,
  PlayerEventMap,
} from "amazon-ivs-player";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import i18next from "i18next";
import NpawPlugin from "npaw-plugin-nwf";
import NpawPluginAdapters from "npaw-plugin-adapters-es5";
import screenfull from "screenfull";
import videojs from "video.js";

import appConfig from "constants/appConfig";
import {
  getForwardButton,
  getRewindButton,
} from "./videojsComponent/videojsSkipButton";
import { getSpacingElement } from "./videojsComponent/videojsSpacingElement";
import { getTheaterModeButton } from "./videojsComponent/videojsTheaterModeButton";
import {
  getOverlayPauseIcon,
  getOverlayPlayIcon,
} from "./videojsComponent/videojsOrverlay";
import {
  getMobileController,
  getMobileControllerForwardButton,
  getMobileControllerPlayButton,
  getMobileControllerRewindButton,
} from "./videojsComponent/videojsMobileController";
import { getFullScreenModeButton } from "./videojsComponent/videojsFullScreenModeButton";
import { getFullscreenCommentButton } from "./videojsComponent/videojsFullscreenCommentButton";
import { getFullScreenModeIvsButton } from "./videojsComponent/videojsFullScreenModeIvsButton";
import { getSettingMenuButton } from "./videojsComponent/videojsSettingMenuButton";
import { getPictureInPictureButton } from "./videojsComponent/videojsPictureInPictureButton";
import { getPictureInPictureOverlay } from "./videojsComponent/videojsPictureInPictureOverlay";
import { getTHEOplayerConfig } from "./StreaminngUtility";
import { attachInterceptorToTHEOplayer } from "./theoplayer";
import { getTheaterModeIvsButton } from "./videojsComponent/videojsTheaterModeIvsButton";
import { getFullscreenCommentIvsButton } from "./videojsComponent/videojsFullscreenCommentIvsButton";
import {
  getIvsOverlayPauseIcon,
  getIvsOverlayPlayIcon,
} from "./videojsComponent/videojsIvsOrverlay";
import {
  getIvsMobileController,
  getIvsMobileControllerPlayButton,
} from "./videojsComponent/videojsIvsMobileController";

import type Component from "video.js/dist/types/component";
import type Player from "video.js/dist/types/player";
import type * as THEOplayer from "theoplayer";
import type { THEOSetting } from "@spwn/types/firebase/firestore";
import type { StreamingType } from "@spwn/types";
import type { ResolvedTHEOPlayerRuntime } from "./videojsComponent/theoplayerRuntime";

import { blurFromActiveElement } from "./videojsComponent/blurFromActiveElement";
import { getVideoJsMultiAngleSwitcherComponent } from "./videojsComponent/multiAngle/videojsMultiAngleSwitcher";
import { getTheoPlayerImportSource } from "./videojsComponent/theoplayerRuntime";
declare module "theoplayer" {
  type VideoJsComponent = {
    addChild: (name: string, options?: {}, index?: number) => void;
    getChild: (name: string) => VideoJsComponent | undefined;
    hide: () => void;
    show: () => void;
  };

  // theoplayerのPlayer型にvideojsプロパティを追加する
  // eslint-disable-next-line @typescript-eslint/no-namespace
  export namespace videojs {
    type ComponentConstructor =
      typeof import("video.js/dist/types/component").default;

    export function registerComponent(
      name: string,
      component: typeof Component
    ): void;
    export function getComponent(name: string): typeof Component;

    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface Player extends ReturnType<
      typeof import("video.js/dist/types/video.js").default
    > {}
  }
}

/**
 * windowオブジェクトの型を拡張して、ブラウザ固有のAPIに対応
 * 以下の実装を参考にしています。
 * @see https://github.com/silvermine/videojs-airplay/blob/master/src/js/lib/hasAirPlayAPISupport.js
 * @see https://github.com/silvermine/videojs-airplay/blob/master/src/js/components/AirPlayButton.js
 */
declare global {
  interface Window {
    /**
     * AirPlayの利用可否を判定するためのAPI
     * @see https://developer.apple.com/documentation/webkitjs/webkitplaybacktargetavailabilityevent
     */
    WebKitPlaybackTargetAvailabilityEvent?: Function;
    /**
     * Google Cast SDKが提供するAPI
     * @see https://developers.google.com/cast/docs/reference/web_sender
     */
    chrome?: {
      cast?: unknown;
    };
    /**
     * Google Cast SDKが提供するAPI
     * @see https://developers.google.com/cast/docs/reference/web_sender
     */
    cast?: unknown;
    /**
     * THEOplayer グローバルオブジェクト
     */
    THEOplayer: typeof THEOplayer;
  }
}

export type AWSIVSPlayer = Player & VideoJSIVSTech & VideoJSQualityPlugin;

type VideoPlayer = {
  autoPlay: (autoPlay: boolean) => void;
  play: () => void;
  pause: () => void;
  paused: () => boolean;
  currentTime: number;
  setCurrentTime: (seconds: number) => void;
  muted: () => boolean;
  setMuted: (value: boolean) => void;
  setVolume: (volume: number) => void;
  poster: (src: string) => void;
  currentSrc: () => string | undefined;
  seekable: () => THEOplayer.TimeRanges;
  destroy: () => void;
  getHTMLVideoElement: () => HTMLVideoElement;
  registerNPAWPlugin: ({
    vid,
    duration,
    isLive,
  }: {
    vid: string;
    isLive: boolean;
    duration: number | undefined;
  }) => void;
  isFullscreen: () => boolean;
  requestFullscreen: () => void;
  /**
   * isFullscreen() が true の場合のみ使用可能
   */
  exitFullscreen: () => void;
};

export type PlainVideoPlayer = VideoPlayer & {
  src: (source: THEOplayer.SourceDescription | undefined) => void;
  addEventListener: <TType extends keyof THEOplayer.PlayerEventMap>(
    type: TType,
    listener: (event: THEOplayer.PlayerEventMap[TType]) => void
  ) => void;
  removeEventListener: <TType extends keyof THEOplayer.PlayerEventMap>(
    type: TType,
    listener: (event: THEOplayer.PlayerEventMap[TType]) => void
  ) => void;

  getCurrentSrc: () => THEOplayer.SourceDescription | undefined;
  setHasLowLatencyToggle: (hasLowLatencyToggle: boolean) => void;
  /**
   * Chromecast にキャストしたときだけレシーバへ渡すソースを設定する。
   * undefined を渡すと live の cast ボタンが消える。
   */
  setCastSource: (castSource: THEOplayer.SourceDescription | undefined) => void;
  network: THEOplayer.Player["network"];
  /**
   * Mux Data など、THEOplayer のインスタンスを直接必要とする SDK 連携のために公開する
   */
  theoPlayer: THEOplayer.Player;
};

export type IVSVideoPlayer = VideoPlayer & {
  /**
   * 再生に使われている video 要素を返す。
   * getHTMLVideoElement() が返すのは videojs のコンテナ要素なので、そちらとは別物
   */
  getVideoElement: () => HTMLVideoElement | undefined;
  src: ({ src }: { src: string }) => void;
  addIVSPlayerEventListener: <K extends keyof PlayerEventMap>(
    name: K,
    fn: (payload: PlayerEventMap[K]) => void
  ) => void;
  addEventListener: <TType extends keyof THEOplayer.PlayerEventMap>(
    type: TType | "Playing",
    listener: (event: THEOplayer.PlayerEventMap[TType]) => void
  ) => void;
  removeIVSPlayerEventListener: <K extends keyof PlayerEventMap>(
    name: K,
    fn: (payload: PlayerEventMap[K]) => void
  ) => void;
  removeEventListener: <TType extends keyof THEOplayer.PlayerEventMap>(
    type: TType | "Playing",
    listener: (event: THEOplayer.PlayerEventMap[TType]) => void
  ) => void;
  getCurrentSrc: () => string | undefined;
};

const createCommonPlayerMethods = (
  player: THEOplayer.Player | AWSIVSPlayer
) => {
  return {
    play() {
      player.play();
    },

    pause() {
      player.pause();
    },

    paused() {
      return typeof player.paused === "function"
        ? player.paused()
        : player.paused;
    },

    setCurrentTime(seconds: number) {
      player.currentTime = seconds;
    },

    muted(): boolean {
      if (typeof player.muted === "function") {
        return player.muted() ?? false;
      } else {
        return player.muted;
      }
    },

    setMuted(value: boolean) {
      if (typeof player.muted === "function") {
        player.muted(value);
      } else {
        player.muted = value;
      }
    },

    setVolume(volume: number) {
      if (typeof player.volume === "function") {
        player.volume(volume);
      } else {
        player.volume = volume;
      }
    },

    poster(src: string) {
      player.poster = src;
    },

    currentSrc() {
      if (typeof player.src === "function") {
        return player.src(undefined);
      }
      return player.src;
    },

    seekable() {
      return typeof player.seekable === "function"
        ? player.seekable()
        : player.seekable;
    },

    registerNPAWPlugin({
      vid,
      duration,
      isLive,
    }: {
      vid: string;
      isLive: boolean;
      duration: number | undefined;
    }) {
      if (appConfig.Youbora.accountCode !== undefined) {
        const npawPlugin = new NpawPlugin(appConfig.Youbora.accountCode);
        npawPlugin.registerAdapterFromClass(
          player,
          NpawPluginAdapters.video.Videojs
        );

        npawPlugin.setVideoOptions({
          "user.name": firebase.auth()?.currentUser?.uid ?? "undefined",
          "content.title": vid,
          "content.isLive": isLive,
          ...(duration !== undefined && { "content.duration": duration }),
        });
      }
    },
  };
};

export const createPlainVideoPlayer = ({
  theoPlayerRuntime,
  setting,
  fullscreenElementRef,
  streamingType,
  videoId,
  hasLowLatencyToggle,
  useLLHLS,
  isLowLatencyEnabled,
  isTheaterModeEnabled,
  isForceTheaterMode,
  toggleTheaterMode,
  toggleLowLatency,
  handleSwitchMultiAngleVideo,
}: {
  theoPlayerRuntime: ResolvedTHEOPlayerRuntime;
  setting: THEOSetting;
  fullscreenElementRef?: React.RefObject<HTMLDivElement>;
  streamingType: StreamingType;
  videoId: string;
  hasLowLatencyToggle: boolean;
  useLLHLS: boolean;
  isLowLatencyEnabled: boolean;
  isTheaterModeEnabled: boolean;
  isForceTheaterMode: boolean;
  toggleTheaterMode: () => void;
  toggleLowLatency: () => void;
  handleSwitchMultiAngleVideo: (videoId: string) => void;
}): PlainVideoPlayer => {
  const { license } = getTHEOplayerConfig();
  const { THEOplayer, libraryLocation: resolvedLibraryLocation } =
    theoPlayerRuntime;
  const element = document.querySelector("#theoplayer-container");

  const player = new THEOplayer.Player(element as HTMLElement, {
    /* mobileではnativeのfullscreenを使用する */
    allowNativeFullscreen: true,

    // initialRendition: 'low',
    /**
     * https://portal.theoplayer.com/app/bdac9bf2-8a66-460c-944d-8235876c0e5c/players/808556fc-fb19-4fd7-8633-20184fda147e
     * 有効期限: 2026-12-30 (UTC) まで再生可、2026-12-31 00:00 UTC 以降は失効 (2026-09-11 に Date を偽装して実測)
     * ドメイン制限なし。CDN のポータルビルド (public.spwn.jp/packages/THEOplayer-x.y.z) はライセンスが
     * 埋め込み済みでこの値を参照しないため、期限更新時はビルドの再作成・再アップロードも必要
     */
    license,
    libraryLocation: resolvedLibraryLocation,
    mutedAutoplay: "all",
    allowMixedContent: true,
    pip: setting.enablePiP
      ? {
          visibility: 0.5,
          position: "bottom-right",
          retainPresentationModeOnSourceChange: true,
        }
      : {},
    ui: {
      fluid: true, // コンテナの幅に合わせてプレイヤーのサイズを調整
      language: i18next.language,
      languages: {
        ja: {
          Automatic: "自動",
          Fullscreen: "全画面",
          Pause: "一時停止",
          Quality: "画質",
          Settings: "設定",
          Speed: "再生速度",
          Normal: "通常",
          Mute: "ミュート",
          Unmute: "ミュート解除",
        },
      },
    },
  });

  // player.src = 'https://wowza.peer5.com/live/smil:bbb_abr.smil/playlist.m3u8';
  player.abr.targetBuffer = setting.abr.targetBuffer;
  player.abr.strategy = setting.abr.strategy;
  player.muted = false; // default is unmuted
  // npmモジュール版ではmutedAutoplayが正常に機能しないため、userActivationを確認してミュート状態を設定する
  // TODO: npmモジュールへの移行が完了したらフィーチャーフラグと合わせて削除する
  // @see https://developer.mozilla.org/ja/docs/Web/API/UserActivation
  if (getTheoPlayerImportSource() === "node-module") {
    const hasUserActivation =
      typeof navigator !== "undefined" &&
      "userActivation" in navigator &&
      !!navigator.userActivation?.hasBeenActive;
    player.muted = !hasUserActivation;
  }
  player.autoplay = true;

  /**
   * @description 配信ステータスがVODの時、"vjs-status-archive"を付与し、Liveの時は削除する
   * @see https://github.com/videojs/video.js/blob/8e4ec97eb9a38dc1e5571b2b4a996df387139f1f/src/js/control-bar/time-controls/duration-display.js#L67-L71
   * @see https://github.com/videojs/video.js/blob/8e4ec97eb9a38dc1e5571b2b4a996df387139f1f/src/js/control-bar/time-controls/duration-display.js#L43C22-L43C36
   */
  const registerArchiveStatusClassEvent = () => {
    player.addEventListener("loadedmetadata", () => {
      // FIXME: theoplayerにはliveTrackerが存在しないため、一旦コメントアウト
      // if (player.theoLive && player.liveTracker.isLive()) {
      //   player.removeClass("vjs-status-archive");
      // } else {
      //   player.addClass("vjs-status-archive");
      // }
    });
  };

  /**
   * CastMenu（ChromecastやAirPlayボタンを内包するメニュー）のセットアップを行います。
   * 各キャストデバイスの利用可否を監視し、どちらか一方が利用可能な場合にのみメニュー全体を表示します。
   *
   * isChromecastDeviceAvailable と isAirPlayDeviceAvailable は、
   * 非同期のイベントリスナー内で値が更新されるため、再代入が可能な `let` を使用しています。
   */

  /**
   * マルチアングル配信において、アングル切替ボタンを追加する
   */

  const registerMultiAngleSwitcher = () => {
    THEOplayer.videojs.registerComponent(
      "MultiAngleSwitcher",
      getVideoJsMultiAngleSwitcherComponent({
        playerType: "THEOPlayer",
        runtime: THEOplayer,
      })
    );

    if (!player.ui.getChild("MultiAngleSwitcher")) {
      player.ui.addChild("MultiAngleSwitcher", { handleSwitchMultiAngleVideo });
    }
  };

  // オーバーレイアイコンを追加する処理
  const registerOverlay = () => {
    THEOplayer.videojs.registerComponent(
      "OverlayPlayIcon",
      getOverlayPlayIcon(THEOplayer)
    );
    THEOplayer.videojs.registerComponent(
      "OverlayPauseIcon",
      getOverlayPauseIcon(THEOplayer)
    );

    if (!player.ui.getChild("OverlayPlayIcon")) {
      player.ui.addChild("OverlayPlayIcon", {}, 0);
    }
    if (!player.ui.getChild("OverlayPauseIcon")) {
      player.ui.addChild("OverlayPauseIcon", {}, 1);
    }

    const handlePlay = () => {
      const playIcon = document.querySelector(".vjs-custom-overlay-play-icon");
      if (playIcon) {
        playIcon.classList.add("active");
        setTimeout(() => {
          playIcon.classList.remove("active");
        }, 1000);
      }
    };

    const handlePause = () => {
      const pauseIcon = document.querySelector(
        ".vjs-custom-overlay-pause-icon"
      );
      if (pauseIcon) {
        pauseIcon.classList.add("active");
        setTimeout(() => {
          pauseIcon.classList.remove("active");
        }, 1000);
      }
    };

    /**
     * @description
     * 配信画面の動画部分がクリックされたときに再生・一時停止アイコンの表示を切り替える。
     * .vjs-techはvideojsが生成する動画要素のクラス名であり動画部分全体を指している。
     * コントローラーを除いた動画部分全体にイベントリスナーを設定するため、.vjs-techに対して設定を行っている。
     * @see https://optiview.dolby.com/docs/theoplayer/how-to-guides/ui/how-to-disable-click-to-pause/
     */
    const videoScreen = document.querySelector(".vjs-tech");
    videoScreen?.addEventListener("click", () => {
      if (player.paused) {
        handlePlay();
      } else {
        handlePause();
      }
      blurFromActiveElement();
    });
  };

  const registerMobileController = () => {
    THEOplayer.videojs.registerComponent(
      "MobileController",
      getMobileController(THEOplayer)
    );
    if (!player.ui.getChild("MobileController")) {
      // モバイルコントローラーを追加する
      player.ui.addChild("MobileController", {}, 1);
    }

    // MobileControllerコンポーネントを取得・なければ何もしない
    const mobileController = player.ui.getChild("MobileController");
    if (!mobileController) {
      return;
    }

    THEOplayer.videojs.registerComponent(
      "MobileControllerPlayIcon",
      getMobileControllerPlayButton(THEOplayer)
    );
    THEOplayer.videojs.registerComponent(
      "MobileControllerRewindButton",
      getMobileControllerRewindButton(THEOplayer)
    );
    THEOplayer.videojs.registerComponent(
      "MobileControllerForwardButton",
      getMobileControllerForwardButton(THEOplayer)
    );

    // モバイル用のメニューを登録
    if (!mobileController.getChild("MobileControllerRewindButton")) {
      mobileController.addChild("MobileControllerRewindButton", {}, 0);
    }
    if (!mobileController.getChild("MobileControllerForwardButton")) {
      mobileController.addChild("MobileControllerForwardButton", {}, 2);
    }
    if (!mobileController.getChild("MobileControllerPlayIcon")) {
      mobileController.addChild("MobileControllerPlayIcon", {}, 1);
    }
  };

  // コントローラーにボタンを追加する処理
  const registerSkipButton = () => {
    THEOplayer.videojs.registerComponent(
      "rewindButton",
      getRewindButton(THEOplayer)
    );
    THEOplayer.videojs.registerComponent(
      "forwardButton",
      getForwardButton(THEOplayer)
    );

    const controlBar = player.ui.getChild("controlBar");

    if (controlBar) {
      if (!controlBar.getChild("rewindButton")) {
        controlBar.addChild("rewindButton", {}, 0);
      }
      if (!controlBar.getChild("forwardButton")) {
        controlBar.addChild("forwardButton", {}, 2);
      }
    }

    // 配信タイプが変更された時に、ボタンの表示/非表示を切り替える
    player.addEventListener("loadedmetadata", () => {
      if (controlBar) {
        // FIXME: theoplayerにはliveTrackerが存在しないため、一旦コメントアウト
        // const rewindButton = controlBar.getChild("rewindButton");
        // const forwardButton = controlBar.getChild("forwardButton");
        // if (player.liveTracker && player.liveTracker.isLive()) {
        //   rewindButton?.hide();
        //   forwardButton?.hide();
        // } else {
        //   rewindButton?.show();
        //   forwardButton?.show();
        // }
      }
    });
  };

  const registerSpacingElement = () => {
    THEOplayer.videojs.registerComponent(
      "SpacingElement",
      getSpacingElement(THEOplayer)
    );
    const controlBar = player.ui.getChild("controlBar");
    if (controlBar) {
      if (!controlBar.getChild("SpacingElement")) {
        controlBar.addChild("SpacingElement", {}, 12);
      }
    }
  };

  const registerTheaterModeButton = () => {
    THEOplayer.videojs.registerComponent(
      "TheaterModeButton",
      getTheaterModeButton(THEOplayer)
    );
    const controlBar = player.ui.getChild("controlBar");
    if (controlBar) {
      if (!controlBar.getChild("TheaterModeButton")) {
        controlBar.addChild(
          "TheaterModeButton",
          {
            isTheaterModeEnabled,
            toggleTheaterMode,
          },
          21
        );
      }
    }
  };

  const registerFullscreenCommentButton = () => {
    // 新規のフルスクリーンコメントボタンを登録する
    THEOplayer.videojs.registerComponent(
      "FullscreenCommentButton",
      getFullscreenCommentButton(THEOplayer)
    );
    const controlBar = player.ui.getChild("controlBar");
    if (controlBar) {
      // 新規のフルスクリーンコメントボタンがなければ追加する
      if (!controlBar.getChild("FullscreenCommentButton")) {
        controlBar.addChild(
          "FullscreenCommentButton",
          {
            fullscreenElementRef,
          },
          22
        );
      }
    }

    const commentBtn: HTMLElement | null = document.querySelector(
      ".vjs-custom-fullscreen-comment-button"
    );
    if (commentBtn) {
      commentBtn.style.display = "none";
      if (screenfull.isEnabled) {
        screenfull.on("change", () => {
          commentBtn.style.display = screenfull.isFullscreen ? "block" : "none";
        });
      }
    }
  };

  const registerFullscreenButton = () => {
    // 新規のフルスクリーンボタンを登録する
    THEOplayer.videojs.registerComponent(
      "FullScreenModeButton",
      getFullScreenModeButton(THEOplayer)
    );
    const controlBar = player.ui.getChild("controlBar");
    if (controlBar) {
      // 新規のフルスクリーンボタンがなければ追加する
      if (!controlBar.getChild("FullScreenModeButton")) {
        controlBar.addChild(
          "FullScreenModeButton",
          {
            fullscreenElementRef,
          },
          23
        );
      }
    }
  };

  const registerPictureInPictureButton = () => {
    // Presentation API を持たないランタイムに差し替わった場合はボタン自体を出さない
    // (theoplayer-legacy 2.92.0 にも API はあるが、念のため防御する)
    if (!player.presentation) return;

    THEOplayer.videojs.registerComponent(
      "PictureInPictureButton",
      getPictureInPictureButton({
        playerType: "THEOPlayer",
        runtime: THEOplayer,
        theoPlayer: player,
      })
    );
    const controlBar = player.ui.getChild("controlBar");
    if (controlBar) {
      if (!controlBar.getChild("PictureInPictureButton")) {
        controlBar.addChild("PictureInPictureButton", {}, 20);
      }
    }

    // PiP 中にブラウザが video 要素内へ出す文言を隠して自前の文言を出す
    THEOplayer.videojs.registerComponent(
      "PictureInPictureOverlay",
      getPictureInPictureOverlay({
        playerType: "THEOPlayer",
        runtime: THEOplayer,
        theoPlayer: player,
      })
    );
    if (!player.ui.getChild("PictureInPictureOverlay")) {
      player.ui.addChild("PictureInPictureOverlay", {});
    }
  };

  let handleLLError: (() => void) | null = null;

  const registerToLLPlayerButton = (
    hasLowLatencyToggle: boolean,
    useLLHLS: boolean,
    isLowLatencyEnabled: boolean
  ) => {
    // THEOplayer内の設定メニューのDOMを取得
    const menuContainer = player.ui.getChild("ControlBarMenuContainer");
    if (!menuContainer) return;
    const settingsButton = menuContainer.getChild("SettingsButton");
    if (!settingsButton) return;

    const menu = settingsButton
      .el()
      .querySelector(
        ".theo-settings-control-menu .theo-menu:not(.theo-submenu):not(.theo-leaf-submenu) .theo-menu-content"
      );
    if (!menu) return;

    const toLLButton = document.createElement("li");
    toLLButton.className =
      "theo-menu-item vjs-menu-item vjs-custom-ll-mode-button"; // THEOのスタイルを継承
    if (!hasLowLatencyToggle) {
      // 低遅延非対応の場合は非表示にする
      toLLButton.classList.add("vjs-hidden");
    }
    toLLButton.style.cursor = "pointer";
    if (useLLHLS) {
      toLLButton.addEventListener("click", () => {
        const checkbox = toLLButton.querySelector<HTMLInputElement>(
          "input[type='checkbox']"
        );
        if (checkbox) {
          checkbox.checked = !checkbox.checked;
        }
        toggleLowLatency();
      });
      /**
       * LL-HLS低遅延モードでエラーが発生した場合、チェックボックスをオフにする
       * TODO: checkboxのon offはredux管理したい
       */
      handleLLError = () => {
        const checkbox = toLLButton.querySelector<HTMLInputElement>(
          "input[type='checkbox']"
        );
        if (checkbox) {
          checkbox.checked = false;
        }
      };
      player.addEventListener("error", handleLLError);
    } else {
      toLLButton.addEventListener("click", toggleLowLatency);
    }

    const toLLButtonTitle = document.createElement("span");
    toLLButtonTitle.className = "theo-settings-control-menu-item-title";
    toLLButtonTitle.innerText = "低遅延";

    // トグルボタンを挿入
    const toLLButtonValue = document.createElement("span");
    toLLButtonValue.className =
      "theo-primary-color theo-settings-control-menu-item-value";
    toLLButtonValue.innerHTML = `
      <style>
      .switch {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 17px;
        pointer-events: none;
      }
      .switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }

      .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
      }
      .slider:before {
        position: absolute;
        content: "";
        height: 10px;
        width: 10px;
        left: 3px;
        bottom: 3px;
        background-color: white;
      }
      input:checked + .slider:before {
        transform: translateX(23px);
      }
      input:checked + .slider {
        background-color: #2196F3;
      }
      input:focus + .slider {
        box-shadow: 0 0 1px #2196F3;
      }

      .slider.round {
        border-radius: 17px;
      }
      .slider.round:before {
        border-radius: 50%;
      }
      </style>

      <label class="switch">
        <input type="checkbox" ${useLLHLS && isLowLatencyEnabled ? "checked" : ""}>
        <span class="slider round"></span>
      </label>
    `;

    toLLButton.appendChild(toLLButtonTitle);
    toLLButton.appendChild(toLLButtonValue);

    menu.appendChild(toLLButton);
  };

  // 配信ステータスに応じてクラスを切り替える
  registerArchiveStatusClassEvent();

  // マルチアングル配信において、アングル切替ボタンを追加する
  registerMultiAngleSwitcher();

  // オーバーレイアイコンを追加する
  registerOverlay();

  registerMobileController();

  // コントローラーにボタンを追加する
  registerSkipButton();
  /**
   * 品質選択メニューはsafariでは表示されないのは仕様です。
   * @see https://balus3d.slack.com/archives/C05U36GAERF/p1751427583708589?thread_ts=1751426647.552499&cid=C05U36GAERF
   */
  // registerQualityMenu();
  // registerMobileMenu();

  registerSpacingElement();
  if (!isForceTheaterMode) {
    registerTheaterModeButton();
  }
  registerFullscreenCommentButton();
  registerFullscreenButton();
  registerPictureInPictureButton();
  // 低遅延メニューを登録する処理
  registerToLLPlayerButton(hasLowLatencyToggle, useLLHLS, isLowLatencyEnabled);

  /**
   * cast 中のレシーバ端末は Cookie を送れないため、live で cast できるのは
   * トークン付きのソースを渡せるときだけ。
   */
  let castSource: THEOplayer.SourceDescription | undefined = undefined;

  const manageCastButtonsVisibility = () => {
    const controlBar = player.ui.getChild("controlBar");
    if (!controlBar) {
      return;
    }

    const chromecastButton = controlBar.getChild("chromecastButton");
    const airplayButton = controlBar.getChild("airplayButton");

    if (streamingType !== "Live" || castSource !== undefined) {
      chromecastButton?.show();
    } else {
      chromecastButton?.hide();
    }

    /**
     * AirPlay はレシーバへ渡すソースを差し替える API が無く、Apple TV に
     * 署名付き Cookie を引き継げないため live では出さない。
     */
    if (streamingType !== "Live") {
      airplayButton?.show();
    } else {
      airplayButton?.hide();
    }
  };

  /**
   * onStart / onJoin の戻り値がレシーバ側に、onStop / onLeave の戻り値が
   * センダー側にロードされる。センダーの source は書き換わらないので、
   * cast 中もブラウザでの再生は署名付き Cookie の経路のまま保たれる。
   *
   * undefined を返すと THEOplayer の既定に落ちる。onStart / onJoin の既定は
   * センダー側のソースをそのままレシーバへ渡すことなので、それでよい。
   *
   * onStop / onLeave の既定は「レシーバ側のソースをセンダーにロードする」で、
   * これはセンダーとレシーバが同じソースである前提のもの。cast 中のレシーバは
   * トークン URL を再生しているため、既定に任せると cast を止めた瞬間から
   * ブラウザがトークン経路に移ってしまう。センダー側のソースを明示して返す。
   *
   * 配信終了や低遅延への切り替えでソースを外していたら、空の sources を返して
   * 何もロードさせない。
   */
  const castConnectionCallback: THEOplayer.ChromecastConnectionCallback = {
    onStart: () => castSource,
    onJoin: () => castSource,
    onStop: () => player.source ?? { sources: [] },
    onLeave: () => player.source ?? { sources: [] },
  };

  /**
   * cast モジュールは THEOplayer の feature に含まれていないと生えないので、
   * 取得できたときだけ登録する。
   */
  const attachCastConnectionCallback = () => {
    const chromecast = player.cast?.chromecast;
    if (chromecast) {
      chromecast.connectionCallback = castConnectionCallback;
    }
  };

  attachCastConnectionCallback();

  // プレイヤーの準備ができた後、または配信情報が読み込まれた後に実行
  player.addEventListener("loadedmetadata", manageCastButtonsVisibility);
  // loadedmetadata が来なくても live で cast ボタンが出たままにならないようにする
  manageCastButtonsVisibility();

  attachInterceptorToTHEOplayer({
    player,
    streamingType,
    videoId,
    isLLHLSUsed: useLLHLS && isLowLatencyEnabled,
  });

  // 追いついたと判定する閾値（秒）。この秒数未満の遅延になったら追いついたとみなす。
  const liveThreshold = 1.5;

  /**
   * @description 現在の再生時間がライブエッジに近いかどうかを判定する
   * @returns {boolean} ライブエッジに近い場合はtrueを返す
   */
  const isLiveEdge = (): boolean => {
    try {
      const { seekable } = player;
      // 再生可能範囲(seekable)が取得できない場合は、ライブエッジにいるとみなす
      if (!seekable || seekable.length === 0) {
        return true;
      }
      // seekable.end(0)は配信の最先端（ライブエッジ）の時間を表す
      const edge = seekable.end(0);
      const now = player.currentTime;
      return now >= edge - liveThreshold;
    } catch (error) {
      console.error("isLiveEdge error:", error);
      // エラーが発生した場合は、ライブエッジにいるとみなす
      return true;
    }
  };

  const handleTimeUpdate = () => {
    // ライブエッジにいて、かつ現在の再生速度が1倍より大きい場合は、再生速度を1倍に戻す。
    if (isLiveEdge() && player.playbackRate > 1) {
      player.playbackRate = 1;
    }
  };
  // 再生時間の更新を監視するリスナーを追加
  player.addEventListener("timeupdate", handleTimeUpdate);

  return {
    theoPlayer: player,
    isFullscreen() {
      return player.ui.isFullscreen() ?? false;
    },
    requestFullscreen() {
      player.ui.requestFullscreen();
    },
    exitFullscreen() {
      player.ui.exitFullscreen();
    },
    getHTMLVideoElement(): HTMLVideoElement {
      return player.ui.el() as HTMLVideoElement;
    },

    src(source: THEOplayer.SourceDescription | undefined) {
      player.source = source;
    },

    addEventListener<TType extends keyof THEOplayer.PlayerEventMap>(
      type: TType,
      listener: (event: THEOplayer.PlayerEventMap[TType]) => void
    ) {
      player.addEventListener(type, listener);
    },

    removeEventListener<TType extends keyof THEOplayer.PlayerEventMap>(
      type: TType,
      listener: (event: THEOplayer.PlayerEventMap[TType]) => void
    ) {
      player.removeEventListener(type, listener);
    },

    destroy() {
      player.removeEventListener("timeupdate", handleTimeUpdate);
      player.removeEventListener("loadedmetadata", manageCastButtonsVisibility);
      const chromecast = player.cast?.chromecast;
      if (chromecast) {
        chromecast.connectionCallback = undefined;
      }
      try {
        if (handleLLError) {
          player.removeEventListener("error", handleLLError);
        }
      } catch (error) {
        console.error("Error removing LL error event listener:", error);
      }
      player.destroy();
    },

    get currentTime(): number {
      const { currentTime } = player;
      if (currentTime === undefined) {
        return 0;
      }
      return currentTime;
    },

    getCurrentSrc(): THEOplayer.SourceDescription | undefined {
      return player.source;
    },

    // 元のプレイヤーのメソッドをプロキシ
    ...createCommonPlayerMethods(player),

    setHasLowLatencyToggle(hasLowLatencyToggle: boolean) {
      const button = player.ui
        .el()
        .querySelector<HTMLLIElement>(".vjs-custom-ll-mode-button");
      if (!button) return;
      if (hasLowLatencyToggle) {
        button.classList.remove("vjs-hidden");
      } else {
        button.classList.add("vjs-hidden");
      }
    },

    setCastSource(nextCastSource: THEOplayer.SourceDescription | undefined) {
      castSource = nextCastSource;
      // 後から cast モジュールが使えるようになった場合に備えて登録し直す
      attachCastConnectionCallback();
      manageCastButtonsVisibility();
    },

    network: player.network,

    autoPlay(autoPlay: boolean) {
      player.autoplay = autoPlay;
    },

    registerNPAWPlugin({
      vid,
      duration,
      isLive,
    }: {
      vid: string;
      isLive: boolean;
      duration: number | undefined;
    }) {
      if (appConfig.Youbora.accountCode !== undefined) {
        const npawPlugin = new NpawPlugin(appConfig.Youbora.accountCode);
        npawPlugin.registerAdapterFromClass(
          player,
          NpawPluginAdapters.video.Videojs
        );

        npawPlugin.setVideoOptions({
          "user.name": firebase.auth()?.currentUser?.uid ?? "undefined",
          "content.title": vid,
          "content.duration": duration,
          "content.isLive": isLive,
        });
      }
    },
  };
};

export const createIVSVideoPlayer = ({
  id,
  fullscreenElementRef,
  isTheaterModeEnabled,
  isForceTheaterMode,
  hasLowLatencyToggle,
  isLowLatencyEnabled,
  toggleTheaterMode,
  toggleLowLatency,
  handleSwitchMultiAngleVideo,
}: {
  id: string | Element;
  fullscreenElementRef: React.RefObject<HTMLDivElement>;
  isTheaterModeEnabled: boolean;
  isForceTheaterMode: boolean;
  hasLowLatencyToggle: boolean;
  isLowLatencyEnabled: boolean;
  toggleTheaterMode: () => void;
  toggleLowLatency: () => void;
  handleSwitchMultiAngleVideo: (videoId: string) => void;
}): IVSVideoPlayer => {
  registerIVSTech(videojs, {
    wasmWorker:
      "https://public.spwn.jp/spwn-steaming/amazon-ivs-player-1.41.0/dist/assets/amazon-ivs-wasmworker.min.js",
    wasmBinary:
      "https://public.spwn.jp/spwn-steaming/amazon-ivs-player-1.41.0/dist/assets/amazon-ivs-wasmworker.min.wasm",
  });

  // register the quality plugin
  // registerIVSQualityPlugin(videojs);

  const player = videojs(
    id,
    // optionのそれぞれの意味は以下のリンクを参照
    // https://videojs.com/guides/options/
    {
      techOrder: ["AmazonIVS"], // ← AWS IVS技術を使用
      autoplay: true, // ← 自動再生でユーザビリティ向上
      fluid: true, // ← レスポンシブサイズ調整
      controls: true, // ← ユーザーコントロールを提供
      responsive: true, // ← デバイス対応
      /* mobileではnativeのfullscreenを使用する */
      allowNativeFullscreen: true,
      // inactive classが付与されるまでの待機時間. controlBarが消えるまでの時間を調整
      inactivityTimeout: 2000,
      controlBar: {
        progressControl: false, // ← シークバーを非表示に設定
        currentTimeDisplay: false, // ← 現在の再生時間表示を非表示に設定
        timeDivider: false, // ← 時間区切りを非表示に設定
        durationDisplay: false, // ← 再生時間表示を非表示に設定
        seekToLive: false, // ← ライブシークボタンを非表示に設定
      },
      userActions: {
        doubleClick: false, // ← ダブルクリックでのフルスクリーン切り替えを無効化
      },
    },
    function () {
      console.warn("IVSPlayer is ready to use");
    }
  ) as AWSIVSPlayer;

  // enable the quality plugin
  // player.enableIVSQualityPlugin();

  const registerFullscreenIvsButton = () => {
    videojs.registerComponent(
      "FullScreenModeIvsButton",
      getFullScreenModeIvsButton()
    );
    const controlBar = player.getChild("controlBar");
    if (controlBar && !controlBar.getChild("FullScreenModeIvsButton")) {
      controlBar.addChild(
        "FullScreenModeIvsButton",
        {
          fullscreenElement: fullscreenElementRef.current,
        },
        23
      );
    } else {
      console.error(
        "Control bar not found, cannot add FullScreenModeIvsButton"
      );
    }
  };

  /**
   * マルチアングル配信において、アングル切替ボタンを追加する
   */
  const registerMultiAngleSwitcher = () => {
    videojs.registerComponent(
      "MultiAngleSwitcher",
      getVideoJsMultiAngleSwitcherComponent({ playerType: "IVS" })
    );

    if (!player.getChild("MultiAngleSwitcher")) {
      player.addChild("MultiAngleSwitcher", { handleSwitchMultiAngleVideo });
    }
  };

  const registerSettingMenu = () => {
    player.ready(() => {
      const {
        IVSQualityMenuItem,
        BackMenuItem,
        QualityNavigatorItem,
        IVSSettingsButton,
      } = getSettingMenuButton();
      videojs.registerComponent(
        "IVSQualityMenuItem",
        IVSQualityMenuItem as unknown as typeof Component
      );
      videojs.registerComponent(
        "IVSSettingsButton",
        IVSSettingsButton as unknown as typeof Component
      );
      videojs.registerComponent(
        "QualityNavigatorItem",
        QualityNavigatorItem as unknown as typeof Component
      );
      videojs.registerComponent(
        "BackMenuItem",
        BackMenuItem as unknown as typeof Component
      );

      const controlBar = player.getChild("controlBar");
      if (!controlBar) return;

      // 右下の各種メニューボタンの内、一番左にいるはずのコンポーネントを基準にする
      const indexBaseComponent =
        controlBar.getChild("TheaterModeIvsButton") ??
        controlBar.getChild("FullscreenCommentIvsButton") ??
        controlBar.getChild("FullScreenModeIvsButton");
      if (!indexBaseComponent) return;

      const index = controlBar.children().indexOf(indexBaseComponent);
      controlBar.addChild(
        "IVSSettingsButton",
        { hasLowLatencyToggle, isLowLatencyEnabled, toggleLowLatency },
        index !== -1 ? index : controlBar.children().length // indexの取得に失敗した場合はfallbackとしてコントロールバーの最後に配置
      );
    });
  };

  const registerFullscreenCommentIvsButton = () => {
    // 新規のフルスクリーンコメントボタンを登録する
    videojs.registerComponent(
      "FullscreenCommentIvsButton",
      getFullscreenCommentIvsButton()
    );
    const controlBar = player.getChild("controlBar");
    if (controlBar && !controlBar.getChild("FullscreenCommentIvsButton")) {
      controlBar.addChild(
        "FullscreenCommentIvsButton",
        {
          fullscreenElementRef,
        },
        14
      );
    }

    const commentBtn: HTMLElement | null = player
      .el()
      .querySelector(".vjs-custom-fullscreen-comment-button");

    if (commentBtn) {
      commentBtn.style.display = "none";
      if (screenfull.isEnabled) {
        screenfull.on("change", () => {
          commentBtn.style.display = screenfull.isFullscreen ? "block" : "none";
        });
      }
    }
  };

  const registerTheaterModeIvsButton = () => {
    videojs.registerComponent(
      "TheaterModeIvsButton",
      getTheaterModeIvsButton()
    );
    const controlBar = player.getChild("controlBar");
    if (controlBar && !controlBar.getChild("TheaterModeIvsButton")) {
      controlBar.addChild(
        "TheaterModeIvsButton",
        {
          isTheaterModeEnabled,
          toggleTheaterMode,
        },
        14
      );
    }
  };

  const registerPictureInPictureIvsButton = () => {
    videojs.registerComponent(
      "PictureInPictureIvsButton",
      getPictureInPictureButton({ playerType: "IVS" })
    );
    const controlBar = player.getChild("controlBar");
    if (controlBar && !controlBar.getChild("PictureInPictureIvsButton")) {
      controlBar.addChild("PictureInPictureIvsButton", {}, 13);
    }

    // PiP 中にブラウザが video 要素内へ出す文言を隠して自前の文言を出す
    videojs.registerComponent(
      "PictureInPictureIvsOverlay",
      getPictureInPictureOverlay({ playerType: "IVS" })
    );
    if (!player.getChild("PictureInPictureIvsOverlay")) {
      player.addChild("PictureInPictureIvsOverlay", {});
    }
  };

  const registerIvsOverlay = () => {
    videojs.registerComponent("OverlayPlayIcon", getIvsOverlayPlayIcon());
    videojs.registerComponent("OverlayPauseIcon", getIvsOverlayPauseIcon());

    if (!player.getChild("OverlayPlayIcon")) {
      player.addChild("OverlayPlayIcon", {});
    }
    if (!player.getChild("OverlayPauseIcon")) {
      player.addChild("OverlayPauseIcon", {});
    }

    const handlePlay = () => {
      const playIcon = player
        .el()
        ?.querySelector(".vjs-custom-overlay-play-icon");
      if (playIcon) {
        playIcon.classList.add("active");
        setTimeout(() => {
          playIcon.classList.remove("active");
        }, 1000);
      }
    };

    const handlePause = () => {
      const pauseIcon = player
        .el()
        ?.querySelector(".vjs-custom-overlay-pause-icon");
      if (pauseIcon) {
        pauseIcon.classList.add("active");
        setTimeout(() => {
          pauseIcon.classList.remove("active");
        }, 1000);
      }
    };

    /**
     * @description
     * 配信画面の動画部分がクリックされたときに再生・一時停止アイコンの表示を切り替える。
     * .vjs-techはvideojsが生成する動画要素のクラス名であり動画部分全体を指している。
     * コントローラーを除いた動画部分全体にイベントリスナーを設定するため、.vjs-techに対して設定を行っている。
     * @see https://optiview.dolby.com/docs/theoplayer/how-to-guides/ui/how-to-disable-click-to-pause/
     */
    const videoScreen = player.el()?.querySelector(".vjs-tech");
    videoScreen?.addEventListener("click", () => {
      if (player.paused()) {
        handlePlay();
      } else {
        handlePause();
      }
    });
  };

  const registerIvsMobileController = () => {
    videojs.registerComponent("MobileController", getIvsMobileController());
    if (!player.getChild("MobileController")) {
      // モバイルコントローラーを追加する
      player.addChild("MobileController", {});
    }

    // MobileControllerコンポーネントを取得・なければ何もしない
    const mobileController = player.getChild("MobileController");
    if (!mobileController) {
      return;
    }

    videojs.registerComponent(
      "MobileIvsControllerPlayIcon",
      getIvsMobileControllerPlayButton()
    );

    if (!mobileController.getChild("MobileIvsControllerPlayIcon")) {
      mobileController.addChild("MobileIvsControllerPlayIcon", {}, 1);
    }
  };

  registerFullscreenIvsButton();
  if (!isForceTheaterMode) {
    registerTheaterModeIvsButton();
  }
  registerFullscreenCommentIvsButton();
  registerPictureInPictureIvsButton();
  registerIvsOverlay();
  registerIvsMobileController();
  registerMultiAngleSwitcher();
  registerSettingMenu();

  return {
    ...createCommonPlayerMethods(player),
    isFullscreen() {
      return player.isFullscreen() ?? false;
    },
    requestFullscreen() {
      player.requestFullscreen();
    },
    exitFullscreen() {
      player.exitFullscreen();
    },
    getHTMLVideoElement(): HTMLVideoElement {
      return player.el() as HTMLVideoElement;
    },
    getVideoElement(): HTMLVideoElement | undefined {
      return player.el()?.querySelector<HTMLVideoElement>("video") ?? undefined;
    },
    addEventListener<TType extends keyof THEOplayer.PlayerEventMap>(
      type: TType | "Playing" | "Ready",
      listener: (event: THEOplayer.PlayerEventMap[TType]) => void
    ) {
      player.on(type, listener);
    },
    removeEventListener<TType extends keyof THEOplayer.PlayerEventMap>(
      type: TType | "Playing",
      listener: (event: THEOplayer.PlayerEventMap[TType]) => void
    ) {
      player.off(type, listener);
    },
    get currentTime(): number {
      const currentTime = player.currentTime();
      if (currentTime === undefined) {
        return 0;
      }
      return currentTime;
    },
    src({ src }: { src: string }) {
      player.src(src);
    },
    getCurrentSrc(): string | undefined {
      return player.src(undefined);
    },
    destroy() {
      player.dispose();
    },
    autoPlay(autoPlay: boolean) {
      player.autoplay(autoPlay);
    },
    addIVSPlayerEventListener<K extends keyof PlayerEventMap>(
      name: K,
      fn: (payload: PlayerEventMap[K]) => void
    ) {
      player.getIVSPlayer().addEventListener(name, fn);
    },
    removeIVSPlayerEventListener<K extends keyof PlayerEventMap>(
      name: K,
      fn: (payload: PlayerEventMap[K]) => void
    ) {
      player.getIVSPlayer().removeEventListener(name, fn);
    },
  };
};
